import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { firstCompany, getGroupDisplayData } from "../../../helper/attribute";
import { getAllParamsFromLocation } from "../../../helper/loc";
import { fixDecimals } from "../../../helper/wodash";
import { selectUserDoc } from "../../../redux/features/userSlice";
import CbfPdf from "../../pdf-report/CbfPdf";
import { getDetails } from "../../results/CombinedTable";
import { getFormName } from "./config";
import { DisplayResultsTableData } from "./other/OfflineYearResults";
import { getCurrentYear } from "../../../helper/time";
import { getResultDates } from "../../results/resultsCalcs";
import { getCurrentPlan } from "../../../helper/plans";
import { selectResults } from "../../../redux/features/appSlice";

let emissionsDetails = {};

// Function to get number of employees with fallback to previous years
const getNumberOfEmployees = (year) => {
  const company = firstCompany();
  if (!company?.metrics) return 1;

  // If year is null/undefined, use current year
  const currentYear = year || new Date().getFullYear();

  // Try to get FTE for the specified year first
  if (company.metrics[currentYear]?.fte) {
    return company.metrics[currentYear].fte;
  }

  // If no value for current year, search backwards from current year to 2019
  for (let searchYear = currentYear - 1; searchYear >= 2019; searchYear--) {
    if (company.metrics[searchYear]?.fte) {
      return company.metrics[searchYear].fte;
    }
  }

  // If no value found in any year, return default of 1
  return 1;
};

const CbfReportDownload = ({
  year,
  callFunctionInsteadOfDownload,
  renderThisInstead,
  renderResultsEmissionsOnly = false,
}) => {

  const results = useSelector(selectResults);
  const userdoc = useSelector(selectUserDoc);
  const numberOfEmployees = getNumberOfEmployees(year);
  const group = useSelector((state) => state.app.groups)?.[0];
  
  // Memoize filtered results to avoid filtering on every render
  const filteredResults = useMemo(() => {
    if (getCurrentPlan() === "free") {
      return results;
    }
    
    return results.filter(r => {
      const resultYear = getResultDates(r).calendarYear;
      return String(year) === String(resultYear);
    });
  }, [results, year]);

  const [emissionParams, setEmissionParams] = useState({
    numberOfEmployees: numberOfEmployees,
    barChartData: {
      officeBased: numberOfEmployees * 5,
      highEnergyOrganizations: numberOfEmployees * 30,
    },
    dataEnteredBy: userdoc?.firstName + " " + userdoc?.lastName,
    companyName: group?.name ?? firstCompany().title,
  });

  useEffect(() => {
    //generate download data
    let excludewtt;
    if (getAllParamsFromLocation().excludewtt === "yes") excludewtt = true;
    if (filteredResults) {
      const emissionsSum = {};
      emissionsDetails = {};
      filteredResults.forEach((result) => {
        let resultresult = result.result ?? 0;
        let resultwtt = result.wtt ?? 0;
        const formName = getFormName(result.name);
        if (result.name === "electricity") {
          const details = {
            text: getDetails(result.name, result, "Market")?.report,
            result: result["marketbased_result"] + result["marketbased_tnd"],
            resultwtt:
              result[`marketbased_wtt_tnd`] + result["marketbased_wtt"],
          };
          // resultresult += emissionsSum[result.name] || 0; //old result
          emissionsDetails["Market Based " + formName] = emissionsDetails[
            "Market Based " + formName
          ]
            ? [...emissionsDetails["Market Based " + formName], details]
            : [details];
          const detailsLocationBased = {
            text: getDetails(result.name, result)?.report,
            result: result["result"] + result["tnd"],
            resultwtt: result[`wtt_tnd`] + result["wtt"],
          };
          // resultresult += emissionsSum[result.name] || 0; //old result
          emissionsDetails["Location Based " + formName] = emissionsDetails[
            "Location Based " + formName
          ]
            ? [
              ...emissionsDetails["Location Based " + formName],
              detailsLocationBased,
            ]
            : [detailsLocationBased];
          emissionsSum["Location Based " + formName] =
            (emissionsSum[formName] || 0) +
            (detailsLocationBased.result ?? 0) +
            (detailsLocationBased.resultwtt ?? 0);
          emissionsSum["Market Based " + formName] =
            (emissionsSum[formName] || 0) +
            (details.result ?? 0) +
            (details.resultwtt ?? 0);
        } else {
          const details = {
            text: getDetails(result.name, result)?.report,
            result: resultresult,
            resultwtt: resultwtt,
          };
          // resultresult += emissionsSum[result.name] || 0; //old result
          emissionsDetails[formName] = emissionsDetails[formName]
            ? [...emissionsDetails[formName], details]
            : [details];
          emissionsSum[formName] =
            (emissionsSum[formName] || 0) +
            (result.result ?? 0) +
            (result.wtt ?? 0);
        }
      });
      Object.keys(emissionsDetails).forEach((key) => {
        emissionParams[key] = {
          emissions: fixDecimals(emissionsSum[key]),
          details: emissionsDetails[key],
          title: key,
        };
      });
      setEmissionParams({ ...emissionParams });
    }
  }, [filteredResults, year]);

  if (renderResultsEmissionsOnly) {
    return <DisplayResultsTableData data={emissionsDetails} />;
  }
  return (
    <div>
      <CbfPdf
        renderThisInstead={renderThisInstead}
        callFunctionInsteadOfDownload={callFunctionInsteadOfDownload}
        key={filteredResults}
        results={filteredResults}
        emissionParams={emissionParams}
        year={year}
      />
    </div>
  );
};

export default CbfReportDownload;
